import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { hrService } from "../services/hrService";

// ── Mock seed data (Commented out but preserved) ────────────────────────────
/*
const seedEmployees = [
  {
    id: "EMP001", name: "Ravi Kumar", email: "ravi.kumar@venus.com",
    phone: "9876543210", department: "engineering", role: "project-manager",
    employmentType: "full_time", status: "active", joiningDate: "2023-01-15",
    designation: "Senior Engineer", workLocation: "Hyderabad",
    reportingManager: "Suresh Babu", salary: 65000,
  },
  {
    id: "EMP002", name: "Priya Sharma", email: "priya.sharma@venus.com",
    phone: "9876543211", department: "accounts", role: "accountant",
    employmentType: "full_time", status: "active", joiningDate: "2023-03-01",
    designation: "Senior Accountant", workLocation: "Hyderabad",
    reportingManager: "Ravi Kumar", salary: 48000,
  },
  {
    id: "EMP003", name: "Anil Reddy", email: "anil.reddy@venus.com",
    phone: "9876543212", department: "telecaller", role: "telecaller",
    employmentType: "full_time", status: "active", joiningDate: "2023-06-10",
    designation: "Telecaller", workLocation: "Vijayawada",
    reportingManager: "Priya Sharma", salary: 28000,
  },
  {
    id: "EMP004", name: "Sneha Patel", email: "sneha.patel@venus.com",
    phone: "9876543213", department: "marketing", role: "sales-exec",
    employmentType: "full_time", status: "probation", joiningDate: "2024-01-20",
    designation: "Sales Executive", workLocation: "Hyderabad",
    reportingManager: "Ravi Kumar", salary: 35000,
  },
  {
    id: "EMP005", name: "Mohammed Ali", email: "mohammed.ali@venus.com",
    phone: "9876543214", department: "field_staff", role: "installation_team",
    employmentType: "contract", status: "active", joiningDate: "2023-09-01",
    designation: "Field Officer", workLocation: "Guntur",
    reportingManager: "Anil Reddy", salary: 22000,
  },
];

const today = new Date().toISOString().split("T")[0];
const seedAttendance = seedEmployees.map((e) => ({
  id: `ATT-${e.id}-${today}`,
  employeeId: e.id,
  employeeName: e.name,
  date: today,
  status: "present",
  checkIn: "09:05",
  checkOut: "",
  remarks: "",
}));

const seedLeaves = [
  {
    id: "LVE001", employeeId: "EMP001", employeeName: "Ravi Kumar",
    leaveType: "casual", fromDate: "2024-07-10", toDate: "2024-07-11",
    days: 2, reason: "Personal work", status: "approved",
    appliedOn: "2024-07-08", approvedBy: "Admin",
  },
  {
    id: "LVE002", employeeId: "EMP002", employeeName: "Priya Sharma",
    leaveType: "sick", fromDate: "2024-07-15", toDate: "2024-07-15",
    days: 1, reason: "Fever", status: "pending",
    appliedOn: "2024-07-14", approvedBy: null,
  },
  {
    id: "LVE003", employeeId: "EMP003", employeeName: "Anil Reddy",
    leaveType: "casual", fromDate: "2024-07-20", toDate: "2024-07-22",
    days: 3, reason: "Family event", status: "rejected",
    appliedOn: "2024-07-18", approvedBy: "Admin",
  },
];

const seedPaySetups = seedEmployees.map((e) => ({
  employeeId: e.id,
  employeeName: e.name,
  grossSalary: e.salary,
  frequency: "monthly",
  components: [
    { name: "Basic Salary", type: "earning", amount: Math.round(e.salary * 0.5) },
    { name: "HRA", type: "earning", amount: Math.round(e.salary * 0.2) },
    { name: "Special Allowance", type: "earning", amount: Math.round(e.salary * 0.2) },
    { name: "Transport Allowance", type: "earning", amount: Math.round(e.salary * 0.1) },
    { name: "PF (Employee)", type: "deduction", amount: Math.round(e.salary * 0.5 * 0.12) },
    { name: "Professional Tax", type: "deduction", amount: 200 },
  ],
}));

const seedPayslips = [
  {
    id: "PSL001", employeeId: "EMP001", employeeName: "Ravi Kumar",
    month: "06", year: "2024", grossEarnings: 65000,
    totalDeductions: 4100, netPay: 60900, status: "paid",
    generatedOn: "2024-06-30", paidOn: "2024-07-01",
    components: seedPaySetups[0].components,
    workingDays: 26, presentDays: 26, leaveDays: 0,
  },
  {
    id: "PSL002", employeeId: "EMP002", employeeName: "Priya Sharma",
    month: "06", year: "2024", grossEarnings: 48000,
    totalDeductions: 3376, netPay: 44624, status: "generated",
    generatedOn: "2024-06-30", paidOn: null,
    components: seedPaySetups[1].components,
    workingDays: 26, presentDays: 25, leaveDays: 1,
  },
];
*/

// ── Context ─────────────────────────────────────────────────────────────────
const HRContext = createContext(null);
export const useHR = () => useContext(HRContext);

let _empId = 6;
let _leaveId = 4;
let _pslId = 3;

export function HRProvider({ children }) {
  const [employees, setEmployees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [paySetups, setPaySetups] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  // Fetch from API on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        console.log("[HRContext] Fetching initial data...");
        const [empRes, attRes, leaveRes, payRes, pslRes, statsRes] = await Promise.all([
          hrService.getEmployees().catch((err) => { console.error("[HRContext] getEmployees failed:", err); return null; }),
          hrService.getAttendance().catch((err) => { console.error("[HRContext] getAttendance failed:", err); return null; }),
          hrService.getLeaves().catch((err) => { console.error("[HRContext] getLeaves failed:", err); return null; }),
          hrService.getPaySetups().catch((err) => { console.error("[HRContext] getPaySetups failed:", err); return null; }),
          hrService.getPayslips().catch((err) => { console.error("[HRContext] getPayslips failed:", err); return null; }),
          hrService.getDashboardStats().catch((err) => { console.error("[HRContext] getDashboardStats failed:", err); return null; }),
        ]);

        console.log("[HRContext] empRes response:", empRes);

        if (empRes?.data?.data?.employees) {
          const list = empRes.data.data.employees.map(e => ({ ...e, id: e.employeeId || e._id }));
          setEmployees(list);
        }
        if (attRes?.data?.data?.attendance) {
          setAttendance(attRes.data.data.attendance.map(a => ({ ...a, id: a._id })));
        }
        if (leaveRes?.data?.data?.leaves) {
          setLeaves(leaveRes.data.data.leaves.map(l => ({ ...l, id: l._id })));
        }
        if (payRes?.data?.data?.paySetups) {
          setPaySetups(payRes.data.data.paySetups.map(s => ({ ...s, id: s._id })));
        }
        if (pslRes?.data?.data?.payslips) {
          setPayslips(pslRes.data.data.payslips.map(p => ({ ...p, id: p._id })));
        }
        if (statsRes?.data?.data) {
          setDashboardStats(statsRes.data.data);
        }
      } catch (error) {
        console.error("[HRContext] Failed to fetch HR data from backend.", error);
      } finally {
        setIsLoading(false);
        setLoadingStats(false);
      }
    };
    fetchInitialData();
  }, []);

  const refreshDashboardStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const res = await hrService.getDashboardStats();
      if (res?.data?.data) {
        setDashboardStats(res.data.data);
      }
    } catch (e) {
      console.error("[HRContext] refreshDashboardStats failed:", e);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  // ── Employees ────────────────────────────────────────────────────────────
  const addEmployee = useCallback(async (data) => {
    try {
      const res = await hrService.createEmployee(data);
      const newEmp = res?.data?.data?.employee;
      if (newEmp) {
        const formatted = { ...newEmp, id: newEmp.employeeId || newEmp._id };
        setEmployees((p) => [formatted, ...p]);
        return formatted;
      }
    } catch (e) {
      console.warn("API addEmployee failed", e);
      throw e;
    }
    return null;
  }, []);

  const updateEmployee = useCallback(async (id, data) => {
    try {
      const res = await hrService.updateEmployee(id, data);
      const updatedEmp = res?.data?.data?.employee;
      if (updatedEmp) {
        const formatted = { ...updatedEmp, id: updatedEmp.employeeId || updatedEmp._id };
        setEmployees((p) => p.map((e) => (e._id === id || e.id === id ? formatted : e)));
        return;
      }
    } catch (e) {
      console.warn("API updateEmployee failed", e);
      throw e;
    }
  }, []);

  const toggleEmployeeStatus = useCallback(async (id) => {
    try {
      const res = await hrService.toggleEmployeeStatus(id);
      const updatedEmp = res?.data?.data?.employee;
      if (updatedEmp) {
        const formatted = { ...updatedEmp, id: updatedEmp.employeeId || updatedEmp._id };
        setEmployees((p) => p.map((e) => (e._id === id || e.id === id ? formatted : e)));
        return;
      }
    } catch (e) {
      console.warn("API toggleEmployeeStatus failed", e);
      throw e;
    }
  }, []);

  const deleteEmployee = useCallback(async (id) => {
    try {
      await hrService.deleteEmployee(id);
      setEmployees((p) => p.filter((e) => e._id !== id && e.id !== id));
    } catch (e) {
      console.warn("API deleteEmployee failed", e);
      throw e;
    }
  }, []);

  // ── Attendance ───────────────────────────────────────────────────────────
  const markAttendance = useCallback(async (record) => {
    try {
      const res = await hrService.markAttendance(record);
      const newAtt = res?.data?.data?.attendance;
      if (newAtt) {
        const formatted = { ...newAtt, id: newAtt._id };
        setAttendance((prev) => {
          const exists = prev.findIndex(a => a.employeeId === formatted.employeeId && a.date === formatted.date);
          if (exists >= 0) {
            const next = [...prev];
            next[exists] = formatted;
            return next;
          }
          return [formatted, ...prev];
        });
        refreshDashboardStats();
      }
    } catch (e) {
      console.warn("API markAttendance failed", e);
    }
  }, [refreshDashboardStats]);

  const bulkMarkAttendance = useCallback(async (records) => {
    try {
      await hrService.bulkMarkAttendance({ records });
      // Fetch fresh data from backend to ensure all bulk records are accurate
      const res = await hrService.getAttendance();
        if (res?.data?.data?.attendance) {
          setAttendance(res.data.data.attendance.map(a => ({ ...a, id: a._id })));
      }
      refreshDashboardStats();
    } catch (e) {
      console.warn("API bulkMarkAttendance failed", e);
    }
  }, [refreshDashboardStats]);

  // ── Leaves ───────────────────────────────────────────────────────────────
  const applyLeave = useCallback(async (data) => {
    try {
      const res = await hrService.createLeave(data);
      const newLeave = res?.data?.data?.leave;
      if (newLeave) {
        const formatted = { ...newLeave, id: newLeave._id };
        setLeaves((p) => [formatted, ...p]);
        return formatted;
      }
    } catch (e) {
      console.warn("API applyLeave failed", e);
    }
    return null;
  }, []);

  const updateLeaveStatus = useCallback(async (id, status, remarks = "") => {
    try {
      const res = await hrService.updateLeaveStatus(id, status, remarks);
      const updatedLeave = res?.data?.data?.leave;
      if (updatedLeave) {
        const formatted = { ...updatedLeave, id: updatedLeave._id };
        setLeaves((p) => p.map((l) => (l._id === id || l.id === id ? formatted : l)));
        refreshDashboardStats();
        return;
      }
    } catch (e) {
      console.warn("API updateLeaveStatus failed", e);
    }
  }, [refreshDashboardStats]);

  const deleteLeave = useCallback(async (id) => {
    try {
      await hrService.deleteLeave(id);
      setLeaves((p) => p.filter((l) => l.id !== id && l._id !== id));
    } catch (e) {
      console.warn("API deleteLeave failed", e);
    }
  }, []);

  // ── Pay Setup ────────────────────────────────────────────────────────────
  const savePaySetup = useCallback(async (employeeId, data) => {
    try {
      const res = await hrService.savePaySetup(employeeId, data);
      const newSetup = res?.data?.data?.paySetup;
      if (newSetup) {
        const formatted = { ...newSetup, id: newSetup._id };
        setPaySetups((p) => {
          const exists = p.find((s) => (s.employee && s.employee._id === employeeId) || s.employeeId === employeeId);
          if (exists) return p.map((s) => ((s.employee && s.employee._id === employeeId) || s.employeeId === employeeId ? formatted : s));
          return [...p, formatted];
        });
        return;
      }
    } catch (e) {
      console.warn("API savePaySetup failed", e);
    }
  }, []);

  // ── Payslips ─────────────────────────────────────────────────────────────
  const generatePayslip = useCallback(async (data) => {
    try {
      const res = await hrService.generatePayslip(data);
      const newPsl = res?.data?.data?.payslip;
      if (newPsl) {
        const formatted = { ...newPsl, id: newPsl._id };
        setPayslips((p) => [formatted, ...p]);
        return formatted;
      }
    } catch (e) {
      console.warn("API generatePayslip failed", e);
    }
    return null;
  }, []);

  const bulkGeneratePayslips = useCallback(async (data) => {
    try {
      const res = await hrService.bulkGeneratePayslips(data);
      if (res && res.data?.status === 'success') {
        try {
          const pslRes = await hrService.getPayslips();
          if (pslRes && pslRes.data?.data) {
            setPayslips(pslRes.data.data.map(p => ({ ...p, id: p._id })));
          }
        } catch (fetchErr) {
          console.warn("Failed to refetch payslips after bulk generation", fetchErr);
        }
        return res.data;
      }
    } catch (e) {
      console.warn("API bulkGeneratePayslips failed", e);
      throw e;
    }
    return null;
  }, []);

  const markPayslipPaid = useCallback(async (id) => {
    try {
      const res = await hrService.updatePayslipStatus(id, "paid");
      const updatedPsl = res?.data?.data?.payslip;
      if (updatedPsl) {
        const formatted = { ...updatedPsl, id: updatedPsl._id };
        setPayslips((p) => p.map((ps) => (ps._id === id || ps.id === id ? formatted : ps)));
        return;
      }
    } catch (e) {
      console.warn("API updatePayslipStatus failed", e);
    }
  }, []);

  const deletePayslip = useCallback(async (id) => {
    try {
      await hrService.deletePayslip(id);
      setPayslips((p) => p.filter((ps) => ps.id !== id && ps._id !== id));
    } catch (e) {
      console.warn("API deletePayslip failed", e);
    }
  }, []);

  return (
    <HRContext.Provider
      value={{
        employees, addEmployee, updateEmployee, deleteEmployee, toggleEmployeeStatus,
        attendance, markAttendance, bulkMarkAttendance,
        leaves, applyLeave, updateLeaveStatus, deleteLeave,
        paySetups, savePaySetup,
        payslips, generatePayslip, bulkGeneratePayslips, markPayslipPaid, deletePayslip,
        dashboardStats, loadingStats, refreshDashboardStats,
        isLoading
      }}
    >
      {children}
    </HRContext.Provider>
  );
}
